import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Meta from "services/Meta";
import { fetchProfile } from "../../api";
import { readSupportAccessPayload } from "./payload";
import { identifyPostHogUser, resetPostHogUser } from "posthog";

/**
 * Where a support session from the Amer central dashboard lands.
 *
 * That dashboard runs on another origin, so it cannot write this app's storage. It mints a
 * token against this tenant and sends the browser here with it in the URL **fragment** — a
 * fragment never reaches a server, so the token stays out of access and proxy logs. This page
 * moves it into storage, strips it from the address bar, and reloads into the app.
 *
 * The profile fetch is not optional. `token` alone satisfies the auth check in App.js, but
 * every gated route reads `user.permissions` out of storage on mount, so arriving without it
 * bounces straight to access-denied.
 */
const SupportAccess = () => {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  // StrictMode mounts an effect twice in development; the payload is consumed once.
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const payload = readSupportAccessPayload(window.location.hash);

    // Clear the fragment before anything can await: it must not survive in the address bar,
    // in history, or in the referrer of the profile request that follows.
    window.history.replaceState(null, "", window.location.pathname);

    if (!payload) {
      setFailed(true);
      return;
    }

    const land = async () => {
      if (localStorage.getItem("token") || localStorage.getItem("user")) {
        resetPostHogUser();
      }
      localStorage.setItem("token", payload.token);

      if (payload.central_access_token) {
        localStorage.setItem(
          "central_access_token",
          payload.central_access_token
        );
      }

      if (payload.language) {
        localStorage.setItem("default-lg", payload.language);
      }

      const language = localStorage.getItem("default-lg") || "ar";

      try {
        const response = await fetchProfile();

        if (!response?.status || !response?.data) {
          throw new Error("profile unavailable");
        }

        localStorage.setItem("user", JSON.stringify(response.data));
        identifyPostHogUser(response.data);
      } catch {
        // A token the tenant will not accept is the likeliest cause, and leaving it in
        // storage would strand the agent on a permission-less shell rather than the login
        // page.
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("central_access_token");
        setFailed(true);

        return;
      }

      // A full load rather than a client-side navigate: App.js reads the token once, at
      // mount, so the authenticated tree only appears on a fresh boot.
      window.location.replace(`/${language}/home`);
    };

    land();
  }, []);

  return (
    <>
      <Meta title={t("supportAccess.title")} />
      <section className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center">
        <h1 className="text-lg font-semibold text-ink">
          {failed ? t("supportAccess.failedTitle") : t("supportAccess.title")}
        </h1>
        <p className="max-w-md text-sm text-ink-body">
          {failed ? t("supportAccess.failedBody") : t("supportAccess.body")}
        </p>
        {failed && (
          <a className="text-sm font-semibold text-primary" href="/login">
            {t("supportAccess.goToLogin")}
          </a>
        )}
      </section>
    </>
  );
};

export default SupportAccess;
